import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Layout.css';
import { FaHome, FaUser, FaFileAlt, FaBars, FaBuilding, FaChartLine, FaCreditCard, FaDollarSign, FaFileContract } from 'react-icons/fa';
import { getAgentConfig } from '../utils/agentConfig';

const STEP_DEFINITIONS = {
  clientInfo:         { href: '/ClientInfo',         text: 'Client',               icon: <FaUser /> },
  corporateInfo:      { href: '/CorporateInfo',      text: 'Corporate',            icon: <FaHome /> },
  contactInfo:        { href: '/ContactInfo',        text: 'Contacts',             icon: <FaFileAlt /> },
  businessInfo:       { href: '/BusinessInfo',       text: 'Business',             icon: <FaBuilding /> },
  volumeInfo:         { href: '/VolumeInfo',         text: 'Volume',               icon: <FaChartLine /> },
  bankInfo:           { href: '/BankInfo',           text: 'Bank',                 icon: <FaCreditCard /> },
  pricingInfo:        { href: '/PricingInfo',        text: 'Pricing',              icon: <FaDollarSign /> },
  termsAndConditions: { href: '/TermsAndConditions', text: 'Terms And Conditions', icon: <FaFileContract /> },
  eSignature:         { href: '/eSignature',         text: 'Signature',            icon: <FaFileAlt /> },
};

const Layout = ({ children, initialFormProgress, pageTitle }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [completedSections, setCompletedSections] = useState(
    JSON.parse(sessionStorage.getItem('completedSections')) || {}
  );
  const location = useLocation();
  const navigate = useNavigate();

   const getSessionStorageWithExpiry = (key) => {
    const itemStr = sessionStorage.getItem(key);
    if (!itemStr) return null;

    const item = JSON.parse(itemStr);
    const now = new Date();

    if (now.getTime() > item.expiry) {
      sessionStorage.removeItem(key);
      return null;
    }

    return item.value;
  };

  const agentId = getSessionStorageWithExpiry("agentId");
  const agentConfig = getAgentConfig(agentId);
 

  useEffect(() => {
    if (!agentId) navigate('/login');
    else {
      // Set default selected index to 0 when the user logs in
      const sidebar = document.getElementById('sidebar');
      if (sidebar) {
        sidebar.selectedIndex = 0;
        sessionStorage.setItem('sidebarIndex', 0);
      }
    }
  }, [agentId, navigate]);

  // Sync completedSections from sessionStorage when route changes (steps marked complete on form submit only, not on visit)
  useEffect(() => {
    const stored = JSON.parse(sessionStorage.getItem('completedSections')) || {};
    setCompletedSections(stored);
  }, [location.pathname]);

  const handleLogout = () => {
    // Save selected index before logging off
    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      sessionStorage.setItem('sidebarIndex', sidebar.selectedIndex);
    }
    sessionStorage.removeItem('completedSections'); // Optional: Clear completed sections on logout
    sessionStorage.removeItem('sidebarIndex');
    // Add your session expiration logic here
    navigate('/login');
  };

  const { logo: logo1, logoClass, sidebarSteps } = agentConfig;
  const sidebarItems = sidebarSteps.map(key => ({ ...STEP_DEFINITIONS[key], key }));

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  return (
    // Inside your Layout component's return statement
<div className="layout">
  <aside className={`sidebar ${isSidebarOpen ? 'open' : ''}`} id="sidebar">
    <img src={logo1} alt="Company Logo" className={`logo${logoClass ? ' ' + logoClass : ''}`} />
    <ul>
      {sidebarItems.map((item, index) => (
        <li key={index} className={completedSections[item.key] ? 'enabled' : 'disabled'}>
          <a
            href={completedSections[item.key] ? item.href : '#'}
            onClick={(e) => !completedSections[item.key] && e.preventDefault()}
            className={completedSections[item.key] ? 'enabled-link' : 'disabled-link'}
          >
            <span className="icon">{item.icon}</span>
            {item.text}
          </a>
        </li>
      ))}
    </ul>
  </aside>

  <div className="content-area-wrapper">
    <div className="sticky-page-header">
      {/* This is the actual "top" bar of the main content, containing client info and the mobile menu btn */}
      <div className="main-content-header">
        <button className="mobile-menu-btn" onClick={toggleSidebar}>
          <FaBars />
        </button>

        {/* Client Info (AgentId, Logout, About Us, Contact Us) */}
        {/* Wrapper for the line above client container */}
        <div className="client-info-section-wrapper">
          <div className="client-container">
            <div className="agent-id-group">
              <span className="client-label">AgentId:</span>
              <span className="client-name">{agentId}</span>
            </div>
            <div className="agent-actions">
              <a href='/AboutUs' className="action-button">About Us</a>
              <a href='/ContactUs' className="action-button">Contact Us</a>
            </div>
          </div>
        </div>
      </div>
      <div className="separator-line title-separator"></div>
      {pageTitle && <h1 className="section-title">{pageTitle}</h1>}
      <div className="separator-line title-separator"></div>
    </div>
    {/* The rest of your page content, including title and children */}
    <div className="main-content"> {/* This will get the white background and rounded corners */}
      {children}
    </div>
  </div>
</div>
  );
};

export default Layout;
